// Phase 27.x — Guided, step-based event creation.
// Step 1: pick an event type (7 canonical types, each with a "Use this for…"
// hint). Step 2: capture the basics. On Continue the parent opens the full
// Event form drawer pre-seeded with the chosen type's category + defaults, so
// only the fields relevant to that type are shown.

import React, { useState, useEffect } from 'react';
import {
  Megaphone, GraduationCap, Building2, Users, UserPlus, BadgeCheck, Award,
  ArrowLeft, ArrowRight, Calendar as CalendarIcon,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { EVENT_TYPE_WIZARD, TIMEZONES, LANGUAGE_OPTIONS } from './eventFormHelpers';
import DateTimeField from '@/components/common/DateTimeField';

const ICONS = { Megaphone, GraduationCap, Building2, Users, UserPlus, BadgeCheck, Award };

export default function EventTypeWizard({ open, onClose, onContinue, defaultTimezone = 'America/Los_Angeles' }) {
  const [step, setStep] = useState(1);
  const [typeKey, setTypeKey] = useState(null);
  const [basics, setBasics] = useState({
    title: '', description: '', start_local: '', end_local: '',
    timezone: defaultTimezone, language: '',
  });

  useEffect(() => {
    if (open) {
      setStep(1);
      setTypeKey(null);
      setBasics({ title: '', description: '', start_local: '', end_local: '', timezone: defaultTimezone, language: '' });
    }
  }, [open, defaultTimezone]);

  const selectedType = EVENT_TYPE_WIZARD.find((t) => t.key === typeKey);
  const upd = (k, v) => setBasics((b) => ({ ...b, [k]: v }));

  const pickType = (k) => { setTypeKey(k); setStep(2); };

  const handleContinue = () => {
    if (!basics.title.trim()) { toast.error('Title is required'); return; }
    if (!basics.start_local) { toast.error('Start date & time is required'); return; }
    if (!basics.end_local) { toast.error('End date & time is required'); return; }
    if (basics.end_local < basics.start_local) { toast.error('End must be at or after start'); return; }
    const payload = {
      title: basics.title.trim(),
      description: basics.description?.trim() || '',
      start_local: basics.start_local,
      end_local: basics.end_local,
      timezone: basics.timezone,
      language: basics.language || '',
    };
    onContinue?.(typeKey, payload);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-2xl p-0 overflow-hidden" data-testid="event-type-wizard">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="text-xl font-heading">
            {step === 1 ? 'Create an event' : 'Event details'}
          </DialogTitle>
          <DialogDescription>
            {step === 1
              ? 'Choose the type of event. This controls the fields you’ll see next.'
              : `${selectedType?.label} — add the basics. You can fine-tune everything in the next step.`}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="px-6 pb-6 grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto">
            {EVENT_TYPE_WIZARD.map((t) => {
              const Icon = ICONS[t.icon] || CalendarIcon;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => pickType(t.key)}
                  className="group flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left transition hover:border-sky-400 hover:shadow-md"
                  data-testid={`event-type-${t.key}`}
                >
                  <span className="shrink-0 rounded-lg bg-sky-50 p-2 text-sky-600 group-hover:bg-sky-100">
                    <Icon size={20} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-slate-900">{t.label}</span>
                    <span className="block text-xs text-slate-500 mt-0.5 leading-snug">{t.useFor}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {step === 2 && (
          <div className="px-6 pb-4 space-y-4 max-h-[70vh] overflow-y-auto">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Title</Label>
              <Input
                value={basics.title}
                onChange={(e) => upd('title', e.target.value)}
                placeholder="e.g. Fall Community Health Fair"
                className="mt-1"
                data-testid="wizard-title-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Description</Label>
              <Textarea
                rows={3}
                value={basics.description}
                onChange={(e) => upd('description', e.target.value)}
                className="mt-1"
                data-testid="wizard-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Start</Label>
                <div className="mt-1"><DateTimeField value={basics.start_local} onChange={(v) => upd('start_local', v)} testId="wizard-start-input" /></div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">End</Label>
                <div className="mt-1"><DateTimeField value={basics.end_local} onChange={(v) => upd('end_local', v)} testId="wizard-end-input" /></div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Timezone</Label>
                <Select value={basics.timezone} onValueChange={(v) => upd('timezone', v)}>
                  <SelectTrigger className="mt-1" data-testid="wizard-timezone-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Language</Label>
                <Select value={basics.language || undefined} onValueChange={(v) => upd('language', v)}>
                  <SelectTrigger className="mt-1" data-testid="wizard-language-select"><SelectValue placeholder="Not specified" /></SelectTrigger>
                  <SelectContent>
                    {LANGUAGE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between gap-3 border-t border-slate-200 px-6 py-4 bg-slate-50">
          {step === 2 ? (
            <Button variant="ghost" onClick={() => setStep(1)} data-testid="wizard-back-btn">
              <ArrowLeft size={15} className="mr-1" /> Back
            </Button>
          ) : <span />}
          {step === 2 && (
            <Button onClick={handleContinue} data-testid="wizard-continue-btn">
              Continue to details <ArrowRight size={15} className="ml-1" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
